import React from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { toast } from "react-toastify";

import {
  API_get_learning_media_library,
  API_post_learning_file_from_document_image,
} from "../../../constants";
import {
  getRightworkAPI,
  postRightworkAPI,
} from "../requests/utilities/requests";

/**
 * Reusable image picker over the company's LMS media:
 *   - "Your media": LearningFile images — manager uploads plus
 *     AI-generated card/section/cover art. Selecting one returns it
 *     directly.
 *   - "From your documents": photos extracted from knowledge-hub
 *     documents (vision-captioned). Selecting one COPIES it into a
 *     fresh LearningFile server-side first, so LMS content never
 *     depends on knowledge-hub lifecycle.
 *
 * `onSelect({ uuid, url, name })` always receives a LearningFile shape,
 * so consumers (deck cards, covers, future section/quiz integrations)
 * can use it wherever an upload result is used today.
 */
export default function MediaLibraryModal({ open, onClose, companyId, onSelect }) {
  const [tab, setTab] = React.useState("media"); // "media" | "documents"
  const [query, setQuery] = React.useState("");
  const [debouncedQuery, setDebouncedQuery] = React.useState("");
  const [learningFiles, setLearningFiles] = React.useState([]);
  const [documentImages, setDocumentImages] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState(false);
  // uuid of the document image currently being copied — disables its
  // tile so a double-click can't create two LearningFile copies.
  const [copyingUuid, setCopyingUuid] = React.useState(null);
  const searchInputRef = React.useRef(null);

  // Debounce the server-side search so we don't fetch per keystroke.
  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  React.useEffect(() => {
    if (open) {
      setTab("media");
      setQuery("");
      setDebouncedQuery("");
      const t = setTimeout(() => searchInputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  React.useEffect(() => {
    if (!open || !companyId) return;
    let cancelled = false;
    async function load() {
      // Clear before fetching — same stale-scope discipline as
      // ContentSourcePicker: a failed refetch must never leave
      // another query's (or company's) rows selectable.
      setLearningFiles([]);
      setDocumentImages([]);
      setLoading(true);
      setLoadError(false);
      try {
        const res = await getRightworkAPI(API_get_learning_media_library, {
          company_id: companyId,
          q: debouncedQuery,
        });
        if (cancelled) return;
        const payload = res?.data?.redux?.payload;
        setLearningFiles(payload?.learning_files || []);
        setDocumentImages(payload?.document_images || []);
      } catch (err) {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [open, companyId, debouncedQuery]);

  // Both pick paths AWAIT onSelect and keep the modal open (tile busy,
  // closes blocked) until it resolves. Consumers whose onSelect does
  // async work — e.g. the quiz editor copies the LearningFile into a
  // FileUpload and then writes state by captured question/answer
  // index — rely on this: the underlying editor stays untouchable, so
  // rows can't be reordered/removed mid-flight and shift the index.
  // Sync consumers resolve immediately and see no difference.
  const handlePickLearningFile = async (lf) => {
    if (copyingUuid) return;
    setCopyingUuid(lf.uuid);
    try {
      await onSelect?.({ uuid: lf.uuid, url: lf.url, name: lf.name });
      onClose?.();
    } catch (err) {
      const msg =
        err?.response?.data?.system?.message ||
        "Couldn't use that image. Please try again.";
      toast.error(msg, { autoClose: 3000 });
    } finally {
      setCopyingUuid(null);
    }
  };

  const handlePickDocumentImage = async (di) => {
    if (copyingUuid) return;
    setCopyingUuid(di.uuid);
    try {
      const res = await postRightworkAPI(
        API_post_learning_file_from_document_image,
        {
          company_id: companyId,
          document_image_uuid: di.uuid,
        },
      );
      const lf = res?.data?.redux?.payload?.learning_file;
      if (!lf?.uuid) throw new Error("Copy did not return a file");
      await onSelect?.({ uuid: lf.uuid, url: lf.url || di.url, name: lf.name });
      onClose?.();
    } catch (err) {
      const msg =
        err?.response?.data?.system?.message ||
        "Couldn't use that image. Please try again.";
      toast.error(msg, { autoClose: 3000 });
    } finally {
      setCopyingUuid(null);
    }
  };

  const items = tab === "media" ? learningFiles : documentImages;

  // No closing mid-pick: bailing while the copy is in flight would
  // hand control back to the editor before the captured target is
  // applied.
  return (
    <Modal
      open={open}
      onClose={() => {
        if (!copyingUuid) onClose?.();
      }}
      aria-labelledby="media-library-title"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(860px, 94vw)",
          maxHeight: "min(82vh, 760px)",
          bgcolor: "background.paper",
          borderRadius: "12px",
          boxShadow: 24,
          display: "flex",
          flexDirection: "column",
          outline: "none",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2
            id="media-library-title"
            className="text-base font-semibold text-slate-900"
          >
            Choose an image
          </h2>
          <button
            onClick={() => {
              if (!copyingUuid) onClose?.();
            }}
            className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Tabs + search */}
        <div className="px-5 pt-4 pb-3 space-y-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setTab("media")}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border inline-flex items-center gap-1.5 transition-colors ${
                tab === "media"
                  ? "bg-violet-700 text-white border-violet-700"
                  : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
              }`}
            >
              <CollectionsOutlinedIcon style={{ fontSize: 14 }} />
              Your media
            </button>
            <button
              onClick={() => setTab("documents")}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border inline-flex items-center gap-1.5 transition-colors ${
                tab === "documents"
                  ? "bg-violet-700 text-white border-violet-700"
                  : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
              }`}
            >
              <DescriptionOutlinedIcon style={{ fontSize: 14 }} />
              From your documents
              {documentImages.length > 0 && (
                <span className="text-[10px] opacity-80">
                  ({documentImages.length})
                </span>
              )}
            </button>
          </div>
          <div className="relative">
            <SearchIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              style={{ fontSize: 18 }}
            />
            <input
              ref={searchInputRef}
              type="text"
              placeholder={
                tab === "media"
                  ? "Search by file name…"
                  : "Search photo captions and source documents…"
              }
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:border-violet-500 focus:bg-white"
            />
          </div>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="py-12 text-center text-sm text-slate-500">
              Loading images…
            </div>
          ) : loadError ? (
            <div className="py-12 text-center text-sm text-rose-600">
              Couldn't load the library. Close and reopen to retry.
            </div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-500">
              {debouncedQuery
                ? "No matches for that search."
                : tab === "media"
                ? "No images yet — uploads and AI-generated images will appear here."
                : "No document photos yet — photos inside uploaded PDFs, docs, and imported web pages appear here automatically."}
            </div>
          ) : (
            <div className="grid grid-cols-4 md:grid-cols-3 sm:grid-cols-2 gap-3">
              {tab === "media"
                ? learningFiles.map((lf) => (
                    <LibraryTile
                      key={lf.uuid}
                      url={lf.url}
                      label={lf.name}
                      busy={copyingUuid === lf.uuid}
                      disabled={Boolean(copyingUuid)}
                      onClick={() => handlePickLearningFile(lf)}
                    />
                  ))
                : documentImages.map((di) => (
                    <LibraryTile
                      key={di.uuid}
                      url={di.url}
                      label={di.caption}
                      sublabel={di.source_name}
                      busy={copyingUuid === di.uuid}
                      disabled={Boolean(copyingUuid)}
                      onClick={() => handlePickDocumentImage(di)}
                    />
                  ))}
            </div>
          )}
        </div>
      </Box>
    </Modal>
  );
}

function LibraryTile({ url, label, sublabel, onClick, busy, disabled }) {
  const [imgFailed, setImgFailed] = React.useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`group text-left rounded-lg border border-slate-200 overflow-hidden bg-white transition-shadow ${
        disabled && !busy
          ? "opacity-60 cursor-not-allowed"
          : "hover:shadow-md hover:border-violet-300"
      }`}
    >
      <div className="aspect-[4/3] bg-slate-100 relative">
        {url && !imgFailed ? (
          <img
            src={url}
            alt=""
            loading="lazy"
            onError={() => setImgFailed(true)}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-xs">
            Preview unavailable
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
            <span className="w-5 h-5 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
      <div className="px-2 py-1.5">
        <p className="text-[11px] text-slate-800 line-clamp-2 leading-snug">
          {label || "Untitled"}
        </p>
        {sublabel && (
          <p className="text-[10px] text-slate-400 truncate mt-0.5">
            {sublabel}
          </p>
        )}
      </div>
    </button>
  );
}
